import StyleInjector, {FONT_PROPERTY} from '@client/util/styleInjector';
// eslint-disable-next-line import/no-cycle
import store from '@client/store';

import {Category, Logger} from '@simpleinvest/front-end-logger';
// eslint-disable-next-line import/no-cycle
import {verifyOnboardingEmail} from '@client/services/onboarding';
import {ErrorRouteNames} from '@client/routes/errors';
// eslint-disable-next-line import/no-cycle
import {IndexRouteNames} from '@client/routes';
import {displayErrorBanner} from '@client/events/notification-bus';
import {Route, NavigationGuardNext} from 'vue-router';
import {getQueryParamValue} from '@client/routes/helpers';
// eslint-disable-next-line import/no-cycle
import {LoginRouteNames} from '@client/routes/login';

const logger = new Logger({
  category: Category.Routing,
  fileName: 'onboarding.js',
});

export enum OnboardingRouteNames {
  OnboardingPage = 'Onboarding',
  VerifyUser = 'VerifyUser',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const redirectToSegment = ({linkAlias}: any, {query}: Route, next: NavigationGuardNext): void => {
  const path = `/client/${linkAlias}/getstarted`;

  store.dispatch('account/requests/setCurrentRequestId', null);

  return next({path, query: {...query}});
};

const routes = [
  {
    path: `/clientdashboard/verify-user`,
    name: OnboardingRouteNames.VerifyUser,
    meta: {
      publicRoute: true,
    },
    // eslint-disable-next-line max-lines-per-function, max-statements
    beforeEnter: async (to: Route, _from: Route, next: NavigationGuardNext): Promise<void> => {
      const {email: encodedEmailQueryValues, email_code: emailVerificationCodeQueryValues} = to.query;
      const emailVerificationCode = getQueryParamValue(emailVerificationCodeQueryValues);
      const encodedEmail = getQueryParamValue(encodedEmailQueryValues);

      if (encodedEmail && emailVerificationCode) {
        const {riaId} = store.state;
        const decodedEmail = atob(decodeURIComponent(encodedEmail));

        await verifyOnboardingEmail({
          riaId,
          email: decodedEmail,
          emailVerificationCode,
        });

        // eslint-disable-next-line callback-return
        next({
          name: IndexRouteNames.Home,
          query: {
            emailVerified: 'true',
            email: encodedEmail,
          },
        });
      } else {
        const message = 'The verification link is invalid.';

        logger.addBreadcrumb({message});

        displayErrorBanner(message);

        // eslint-disable-next-line callback-return
        next({name: ErrorRouteNames.SystemErrorPage});
      }
    },
  },
  {
    path: '/client/:cdoparameter/getstarted',
    name: 'Onboarding',
    meta: {
      cdo: true,
      CMS: true,
    },
    components: {
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      header: () => import('@client/components/onboarding/OnboardingHeader.vue'),
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      footer: () => import('@client/layouts/Footer.vue'),
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      default: () => import('@client/components/onboarding/sso/onboarding.vue'),
    },
    props: true,
    // eslint-disable-next-line complexity, max-lines-per-function, max-statements
    beforeEnter: async (to: Route, _from: Route, next: NavigationGuardNext): Promise<void> => {
      const {cdoparameter: urlLinkAlias} = to.params;

      try {
        if (!store.state.user.segment) await store.dispatch('user/getClientInformation', null, {root: true});

        await store.dispatch('onboarding/setOnboardingType');
        const {segmentId} = store.state.user.segment;

        if (!segmentId) {
          // There is no segment present with the user, not eligible for CDO

          logger.addBreadcrumb({
            callingFuncName: 'Onboarding route',
            message: `Error:Client has no segment associated`,
          });

          return next({name: LoginRouteNames.LoginPage});
        }

        await store.dispatch('settings/getClientOnboardingSegment', {segmentId});
        const {linkAlias} = store.state.settings;

        StyleInjector.inject(StyleInjector.ONBOARDING_TYPE, FONT_PROPERTY);
        if (urlLinkAlias && linkAlias !== urlLinkAlias) {
          return redirectToSegment({linkAlias: urlLinkAlias}, to, next);
        }
      } catch (error) {
        logger.logException({error});
      }

      // eslint-disable-next-line unicorn/consistent-destructuring
      document.title = store.state.settings.ria.companyLegalName;

      if (to.query.req_id) {
        await store.dispatch('onboarding/getCustodian', to.query.req_id);
        await store.dispatch('user/getAccountAdditionalHeaderInfo', {
          clUserId: store.state.cognitoUser.user.userId,
        });
        await store.dispatch('user/getAccountAdditionalHeadersInfo');
      }

      await Promise.all([
        store.dispatch('account/requests/getActive', store.state.onboarding.custodianId),
        store.dispatch('onboarding/getOnboarding'),
      ]);

      if (store.state.onboarding.requestsSent) {
        await store.dispatch('resetLayout');
        const completionPath = '/complete-sso';

        return next(completionPath);
      }

      await store.state.onboarding.getAllDeps(store, true);
      await store.dispatch('onboarding/updateThirdPartyData');

      if (!store.state.onboarding.active) {
        // go home, you're not CDOing
        return next('/');
      }

      return next();
    },
  },
];

export default routes;
